import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adultGigRequestMessagesApi,
  guardianGigRequestMessagesApi,
  teenGigRequestMessagesApi,
} from "../../lib/gigRequestMessages";
import type {
  GigRequestMessageRequest,
  GigRequestMessageResponse,
  GigRequestMessageSenderRole,
  ScheduleProposalActorRole,
  ScheduleProposalResponse,
} from "../../types/gigTypes";
import { formatDateTime, formatDateTimeInGigTimeZone } from "../../utils/DateUtils";
import { apiErrorToMessage } from "../../utils/ApiUtils";
import {
  containsBlockedContactInfo,
  MESSAGE_CONTACT_INFO_BLOCK_MESSAGE,
  MESSAGE_CONTACT_INFO_NOTICE,
} from "../../utils/ContactInfoUtils";
import Button from "../ui/Button/Button";
import clsx from "clsx";

type GigRequestMessagesPanelProps = {
  requestId: string;
  role: "teen" | "adult" | "guardian";
  participantLabel: string;
  lockedMessage?: string | null;
  description?: string;
  emptyMessage?: string;
  readOnly?: boolean;
  refetchIntervalMs?: number;
  centerHeader?: boolean;
  embedded?: boolean;
  proposals?: ScheduleProposalResponse[] | null;
  timeZone?: string | null;
  perspective?: "teen" | "adult";
  sectionLabel?: string;
  secondaryAction?: ReactNode;
  allowContactInfo?: boolean;
};

const actorLabel = (actorRole: ScheduleProposalActorRole, perspective: "teen" | "adult") => {
  if (perspective === "teen") {
    return actorRole === "TEEN" ? "You" : "Gig poster";
  }
  return actorRole === "TEEN" ? "Teen" : "You";
};

const isConfirmationProposal = (
  proposal: ScheduleProposalResponse,
  previousProposal: ScheduleProposalResponse | undefined,
) =>
  previousProposal
  && proposal.actorRole !== previousProposal.actorRole
  && proposal.proposedStartTime === previousProposal.proposedStartTime
  && proposal.proposedEndTime === previousProposal.proposedEndTime;

const proposalLabel = (
  proposal: ScheduleProposalResponse,
  previousProposal: ScheduleProposalResponse | undefined,
  perspective: "teen" | "adult",
) => {
  if (isConfirmationProposal(proposal, previousProposal)) {
    return `${actorLabel(proposal.actorRole, perspective)} confirmed the proposed time`;
  }
  return `${actorLabel(proposal.actorRole, perspective)} suggested a new start time`;
};

const ownSenderRoleByViewer: Record<GigRequestMessagesPanelProps["role"], GigRequestMessageSenderRole> = {
  teen: "TEEN",
  adult: "ADULT",
  guardian: "ADULT",
};

const ownProposalActorRoleByPerspective: Record<NonNullable<GigRequestMessagesPanelProps["perspective"]>, ScheduleProposalActorRole> = {
  teen: "TEEN",
  adult: "POSTER",
};

const apiByRole = {
  teen: teenGigRequestMessagesApi,
  adult: adultGigRequestMessagesApi,
  guardian: guardianGigRequestMessagesApi,
} satisfies Record<GigRequestMessagesPanelProps["role"], {
  list: (requestId: string) => Promise<GigRequestMessageResponse[]>;
  send?: (requestId: string, payload: GigRequestMessageRequest) => Promise<GigRequestMessageResponse>;
}>;

const GigRequestMessagesPanel = ({
  requestId,
  role,
  participantLabel,
  lockedMessage = null,
  description,
  emptyMessage = "No messages yet. Start the thread when you are ready.",
  readOnly = false,
  refetchIntervalMs,
  centerHeader = false,
  embedded = false,
  proposals,
  timeZone,
  perspective = role === "teen" ? "teen" : "adult",
  sectionLabel = "Messages",
  secondaryAction,
  allowContactInfo = false,
}: GigRequestMessagesPanelProps) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const previousMessageCountRef = useRef(0);
  const canSend = !readOnly && typeof apiByRole[role].send === "function";
  const effectiveRefetchIntervalMs = refetchIntervalMs ?? (role === "guardian" ? 60000 : 15000);

  const messagesQuery = useQuery({
    queryKey: ["gig-request-messages", role, requestId],
    queryFn: () => apiByRole[role].list(requestId),
    enabled: lockedMessage === null,
    refetchInterval: effectiveRefetchIntervalMs,
  });

  const sendMutation = useMutation({
    mutationFn: (payload: GigRequestMessageRequest) => {
      const send = apiByRole[role].send;
      if (!send) {
        throw new Error("Messaging is read only.");
      }
      return send(requestId, payload);
    },
    onSuccess: async () => {
      setDraft("");
      setSubmissionError(null);
      await queryClient.invalidateQueries({ queryKey: ["gig-request-messages", role, requestId] });
    },
    onError: (error) => {
      setSubmissionError(apiErrorToMessage(error, "Unable to send that message."));
    },
  });

  const ownSenderRole = ownSenderRoleByViewer[role];
  const unifiedItems = useMemo(() => {
    const messages = messagesQuery.data ?? [];
    const safeProposals = proposals ?? [];
    const timelineItems = safeProposals.map((proposal, index) => ({
      id: `proposal-${proposal.id}`,
      createdAt: proposal.createdAt,
      type: "proposal" as const,
      proposal,
      previousProposal: index > 0 ? safeProposals[index - 1] : undefined,
    }));
    const messageItems = messages.map((message) => ({
      id: `message-${message.id}`,
      createdAt: message.createdAt,
      type: "message" as const,
      message,
    }));

    return [...timelineItems, ...messageItems].sort((left, right) => {
      const leftTime = new Date(left.createdAt).getTime();
      const rightTime = new Date(right.createdAt).getTime();
      if (leftTime === rightTime) {
        return left.type === "proposal" ? -1 : 1;
      }
      return leftTime - rightTime;
    });
  }, [messagesQuery.data, proposals]);
  const centerReadOnlyLayout = readOnly;
  const trimmedDraft = draft.trim();
  const draftContainsBlockedContactInfo = !allowContactInfo && containsBlockedContactInfo(trimmedDraft);

  useEffect(() => {
    previousMessageCountRef.current = 0;
  }, [requestId, role]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      previousMessageCountRef.current = unifiedItems.length;
      return;
    }

    if (unifiedItems.length > previousMessageCountRef.current) {
      const animationFrame = window.requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
      previousMessageCountRef.current = unifiedItems.length;
      return () => window.cancelAnimationFrame(animationFrame);
    }

    previousMessageCountRef.current = unifiedItems.length;
    return undefined;
  }, [unifiedItems]);

  return (
    <section
      className={clsx(
        "min-w-0 overflow-hidden",
        embedded ? "px-1 py-1 sm:px-1.5 sm:py-1.5" : "rounded-2xl border border-myteengig-border bg-white px-4 py-4",
      )}
    >
      <div
        className={`flex flex-wrap items-center gap-3 ${
          centerReadOnlyLayout || centerHeader ? "justify-center text-center" : "justify-between"
        }`}
      >
        <div className={centerReadOnlyLayout || centerHeader ? "mx-auto text-center" : ""}>
          <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">{sectionLabel}</p>
          <p className="mt-1 text-sm text-myteengig-text-muted">
            {description ?? `Keep communication with the ${participantLabel} inside this request thread.`}
          </p>
        </div>
      </div>

      {lockedMessage ? (
        <div className="mt-4 rounded-2xl border border-dashed border-myteengig-border bg-myteengig-background px-4 py-4">
          <p className="text-sm text-myteengig-text-muted">{lockedMessage}</p>
        </div>
      ) : (
        <>
          <div
            ref={messagesContainerRef}
            className={`mt-3 max-h-80 space-y-3 overflow-x-hidden overflow-y-auto rounded-2xl bg-myteengig-background px-3 py-3 sm:px-4 sm:py-4 ${centerReadOnlyLayout ? "text-center" : ""}`}
          >
            {messagesQuery.isLoading ? <p className="text-sm text-myteengig-text-muted">Loading messages...</p> : null}
            {messagesQuery.error ? (
              <p className="text-sm text-red-600">{apiErrorToMessage(messagesQuery.error, "Failed to load messages.")}</p>
            ) : null}
            {!messagesQuery.isLoading && !messagesQuery.error && unifiedItems.length === 0 ? (
              <p className="text-sm text-myteengig-text-muted">{emptyMessage}</p>
            ) : null}

            {unifiedItems.map((item) => {
              if (item.type === "proposal") {
                const { proposal, previousProposal } = item;
                const isOwnProposal = proposal.actorRole === ownProposalActorRoleByPerspective[perspective];
                const proposalBubbleClassName = centerReadOnlyLayout
                  ? "mx-auto w-full max-w-full border border-myteengig-primary/30 bg-white text-center"
                  : embedded
                    ? isOwnProposal
                      ? "ml-auto w-full max-w-[86%] border border-lime-200 bg-lime-50 sm:max-w-[74%]"
                      : "mr-auto w-full max-w-[86%] border border-myteengig-border bg-white sm:max-w-[74%]"
                    : isOwnProposal
                      ? "ml-auto max-w-[90%] border border-lime-200 bg-lime-50"
                      : "mr-auto max-w-[90%] border border-myteengig-border bg-white";
                const proposalTimeBoxClassName = centerReadOnlyLayout || isOwnProposal ? "bg-white/80" : "bg-myteengig-background/90";

                return (
                  <article
                    key={item.id}
                    className={clsx("min-w-0 rounded-2xl px-4 py-3", proposalBubbleClassName)}
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <p className="text-sm font-semibold text-myteengig-text-primary">
                        {proposalLabel(proposal, previousProposal, perspective)}
                      </p>
                      <span className="text-xs text-myteengig-text-muted">{formatDateTime(proposal.createdAt)}</span>
                    </div>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      <div className={clsx("rounded-xl px-3 py-2", proposalTimeBoxClassName)}>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-myteengig-text-muted">Start</p>
                        <p className="mt-1 text-sm font-medium text-myteengig-text-primary">{formatDateTimeInGigTimeZone(proposal.proposedStartTime, timeZone)}</p>
                      </div>
                      <div className={clsx("rounded-xl px-3 py-2", proposalTimeBoxClassName)}>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-myteengig-text-muted">End</p>
                        <p className="mt-1 text-sm font-medium text-myteengig-text-primary">{formatDateTimeInGigTimeZone(proposal.proposedEndTime, timeZone)}</p>
                      </div>
                    </div>
                    <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-6 text-myteengig-text-primary">
                      {proposal.note?.trim() ? proposal.note : "No timing note was added."}
                    </p>
                  </article>
                );
              }

              const { message } = item;
              if (message.senderRole === "SYSTEM") {
                return (
                  <article
                    key={item.id}
                    className="mx-auto w-full max-w-[92%] rounded-2xl border border-myteengig-border/80 bg-white/90 px-4 py-3 text-center sm:max-w-[84%]"
                  >
                    <div className="flex flex-col items-center justify-center gap-y-1 text-xs text-myteengig-text-muted">
                      <span className="break-words font-semibold uppercase tracking-wide text-myteengig-text-primary">
                        {message.senderDisplayName}
                      </span>
                      <span>{formatDateTime(message.createdAt)}</span>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-myteengig-text-primary">
                      {message.body}
                    </p>
                  </article>
                );
              }
              const isOwn = canSend && message.senderRole === ownSenderRole;
              const messageBubbleClassName = centerReadOnlyLayout
                ? "mx-auto w-full max-w-full border border-myteengig-border bg-white text-center"
                : embedded
                  ? isOwn
                    ? "ml-auto w-full max-w-[86%] border border-lime-200 bg-lime-50 sm:max-w-[74%]"
                    : "mr-auto w-full max-w-[86%] border border-myteengig-border bg-white sm:max-w-[74%]"
                  : isOwn
                    ? "ml-auto max-w-[90%] border border-lime-200 bg-lime-50"
                    : "mr-auto max-w-[90%] border border-myteengig-border bg-white";

              return (
                <article
                  key={item.id}
                  className={`min-w-0 rounded-2xl px-4 py-3 ${messageBubbleClassName}`}
                >
                  <div
                    className={`flex gap-y-1 text-xs text-myteengig-text-muted ${
                      centerReadOnlyLayout
                        ? "flex-col items-center justify-center"
                        : "flex-col items-start sm:flex-row sm:items-center sm:justify-between sm:gap-x-2"
                    }`}
                  >
                    <span className="break-words font-semibold uppercase tracking-wide text-myteengig-text-primary">
                      {isOwn ? "You" : message.senderDisplayName}
                    </span>
                    <span>{formatDateTime(message.createdAt)}</span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-myteengig-text-primary">{message.body}</p>
                </article>
              );
            })}
          </div>

          {canSend ? (
            <div className="mt-4">
              <label htmlFor={`message-draft-${role}-${requestId}`} className="sr-only">
                New message
              </label>
              {!allowContactInfo ? (
                <p className="mb-1 text-xs leading-5 text-myteengig-text-muted">
                  {MESSAGE_CONTACT_INFO_NOTICE}{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-myteengig-primary underline underline-offset-2 hover:text-myteengig-primary-hover"
                  >
                    Review Terms
                  </a>
                </p>
              ) : null}
              <textarea
                id={`message-draft-${role}-${requestId}`}
                value={draft}
                onChange={(event) => {
                  setDraft(event.target.value);
                  if (submissionError) {
                    setSubmissionError(null);
                  }
                }}
                rows={4}
                maxLength={5000}
                className="w-full rounded-2xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none transition focus:border-myteengig-primary"
                placeholder={`Message the ${participantLabel}.`}
              />
              {draftContainsBlockedContactInfo ? <p className="mt-2 text-sm text-red-600">{MESSAGE_CONTACT_INFO_BLOCK_MESSAGE}</p> : null}
              {submissionError ? <p className="mt-2 text-sm text-red-600">{submissionError}</p> : null}
              <div className="mt-3 flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                {secondaryAction ? <div className="w-full sm:w-auto">{secondaryAction}</div> : null}
                <Button
                  className="w-full sm:w-auto"
                  disabled={sendMutation.isPending || trimmedDraft.length === 0 || draftContainsBlockedContactInfo}
                  onClick={() => {
                    if (draftContainsBlockedContactInfo) {
                      setSubmissionError(MESSAGE_CONTACT_INFO_BLOCK_MESSAGE);
                      return;
                    }
                    void sendMutation.mutateAsync({ message: trimmedDraft });
                  }}
                >
                  {sendMutation.isPending ? "Sending..." : "Send message"}
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};

export default GigRequestMessagesPanel;
